"use client";

import type { InsightsWorkingFrame } from "../types";
import { frameSourceLabel } from "./working-frame";

/**
 * Read-only working frame for the investigation sidebar.
 * Caller: InsightsInvestigationSidebar.
 * Renders agent-stored strings as-written (no FE term/period/criteria rewriting).
 * Section enter animation is owned by RailReveal (top→bottom wipe over the whole rail).
 */
export function InsightsWorkingFramePanel({ frame }: { frame: InsightsWorkingFrame | null }) {
  if (!frame?.definition?.trim()) return null;

  const sc = frame.selectionCriteria;
  const systems = sc?.systems ?? [];
  const hasScope = Boolean(sc?.population || sc?.period || sc?.filters?.length);
  const hasTerms = frame.termReadings.length > 0;
  const hasSystems = systems.length > 0;

  return (
    <section className="flex flex-col gap-4 text-[13px] leading-5 text-foreground/90">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Working frame</h2>
        <span className="text-[11px] text-muted-foreground">
          {frameSourceLabel(frame.frameSource)}
        </span>
      </div>

      <p>{frame.definition}</p>

      {hasTerms ? (
        <div className="space-y-2">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Terms
          </h3>
          <dl className="space-y-2">
            {frame.termReadings.map((r) => (
              <div key={`${r.term}:${r.meaning}`}>
                <dt className="text-foreground">{r.term}</dt>
                <dd className="text-muted-foreground">{r.meaning}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      {hasScope ? (
        <div className="space-y-2">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Scope
          </h3>
          <dl className="space-y-1.5">
            {sc?.population ? (
              <div className="grid grid-cols-[5.5rem_1fr] gap-x-2">
                <dt className="text-muted-foreground">Population</dt>
                <dd>{sc.population}</dd>
              </div>
            ) : null}
            {sc?.period ? (
              <div className="grid grid-cols-[5.5rem_1fr] gap-x-2">
                <dt className="text-muted-foreground">Period</dt>
                <dd>{sc.period}</dd>
              </div>
            ) : null}
            {sc?.filters?.map((f) => (
              <div key={f} className="grid grid-cols-[5.5rem_1fr] gap-x-2">
                <dt className="text-muted-foreground">Filter</dt>
                <dd>{f}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      {hasSystems ? (
        <div className="space-y-2">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Systems
          </h3>
          <ul className="space-y-2">
            {systems.map((s) => (
              <li key={`${s.platform}:${s.entity ?? ""}:${s.criteria}`}>
                <div className="text-muted-foreground">
                  {s.platform}
                  {s.entity ? ` · ${s.entity}` : ""}
                </div>
                <code className="mt-0.5 block break-words rounded bg-muted px-1.5 py-1 font-mono text-[12px] text-foreground [overflow-wrap:anywhere]">
                  {s.criteria}
                </code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
